import type {
  ExcelSpreadsheetTemplateFieldsFragment,
  ExcelSpreadsheetTemplateInput,
  ExcelSpreadsheetTemplateTabInput
} from '~/api/graphql';

/** Form-state representation of a tab. Extends the GraphQL input with the
 *  read-only, workbook-derived fields the editor needs to render the card
 *  (worksheet name and the guidance ingestion extracted). Those extra fields
 *  are stripped before the tab is sent over the wire. */
export type ExcelTemplateTabFormEntry = ExcelSpreadsheetTemplateTabInput & {
  name: string;
  fillGuidance: string;
};

/** Form-state representation of the full Excel template input. The workbook is
 *  a single file, so unlike Word templates there is no per-tab upload: tabs
 *  carry authoring metadata only. */
export type ExcelTemplateFormState = Omit<ExcelSpreadsheetTemplateInput, 'tabs'> & {
  tabs: ExcelTemplateTabFormEntry[];
};

/** Map a server-loaded template into editor form state. */
export const formStateFromExcelTemplate = (
  template: ExcelSpreadsheetTemplateFieldsFragment,
  visibility: ExcelSpreadsheetTemplateInput['visibility']
): ExcelTemplateFormState => ({
  title: template.title || '',
  description: template.description || '',
  fillGuidance: template.fillGuidance ?? '',
  templateFileId: template.templateFile?.id ?? null,
  visibility,
  tabs: template.tabs.map(
    (tab): ExcelTemplateTabFormEntry => ({
      id: tab.id,
      name: tab.name,
      fillGuidance: tab.fillGuidance,
      setupGuidance: tab.setupGuidance ?? null,
      static: tab.static,
      required: tab.required
    })
  )
});

/** Serialize the author-editable tab metadata into `FormData` under the `tabs`
 *  key, dropping the workbook-derived fields the server owns. */
export const appendExcelTabsToFormData = (
  formData: FormData,
  tabs: ExcelTemplateTabFormEntry[]
) => {
  formData.set(
    'tabs',
    JSON.stringify(
      tabs.map((tab) => ({
        id: tab.id,
        setupGuidance: tab.setupGuidance ?? null,
        static: tab.static,
        required: tab.required
      }))
    )
  );
};
